import {NestedCard} from '@forecast-it/design-system';
import React, {useMemo} from 'react';
import {useIntl} from 'react-intl';
import {MODAL_TYPE, showModal} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import {BaselineEstimateTables} from './BaselineEstimateTables';
import {
	BaselineItem,
	Company,
	ContextMenuOption,
	DeliverableEdges,
	EyeOptionMap,
	GroupedBaselineTotals,
	Phase,
	ProjectPhaseDeliverable,
} from './BaselineTypes';
import {FinancialsSummary} from './FinancialsSummary';
import {getTotals} from './Util';
import {useDeliverableLogic} from './BaselineLogic';

export const DeliverableCard = ({
	phaseDeliverable,
	currency,
	eyeOptionMap,
	editMode,
	company,
	phase,
	setProjectBaselineItems,
	baselineItems,
	groupedProjectTotals,
	projectBudget,
	isFixedPrice,
	projectDeliverables,
	setPhaseDeliverables,
	baselineLocked,
	disabledRoleIds,
	budgetType,
}: {
	phaseDeliverable?: ProjectPhaseDeliverable;
	currency: string;
	company: Company;
	eyeOptionMap?: EyeOptionMap;
	editMode?: boolean;
	phase: Phase;
	setProjectBaselineItems?: React.Dispatch<React.SetStateAction<BaselineItem[]>>;
	setPhaseDeliverables?: React.Dispatch<React.SetStateAction<ProjectPhaseDeliverable[]>>;
	baselineItems?: BaselineItem[];
	groupedProjectTotals?: GroupedBaselineTotals;
	projectBudget?: number;
	isFixedPrice?: boolean;
	projectDeliverables?: DeliverableEdges;
	baselineLocked?: boolean;
	disabledRoleIds?: string[];
	budgetType?: string | null;
}): JSX.Element => {
	const {formatMessage} = useIntl();
	const {deliverable} = phaseDeliverable || {};

	const phaseDeliverableBaselineTotals = useMemo(() => {
		return getTotals(baselineItems || []);
	}, [baselineItems]);

	const {handleChangeDeliverable, handleDeleteDeliverable} = useDeliverableLogic({
		phaseDeliverable,
		setPhaseDeliverables,
		setProjectBaselineItems,
		phaseId: phase.id,
	});

	const showChangeDeliverableModal = () => {
		showModal({
			type: MODAL_TYPE.CHANGE_DELIVERABLE_MODAL,
			projectDeliverables,
			currentDeliverable: deliverable,
			callback: handleChangeDeliverable,
		});
	};

	const showDeleteDeliverableModal = () => {
		showModal({
			type: MODAL_TYPE.REMOVE_DELIVERABLE_FROM_PHASE_MODAL,
			callback: handleDeleteDeliverable,
		});
	};

	const showManageDeliverablesModal = () => {
		showModal({
			type: MODAL_TYPE.MANAGE_DELIVERABLES_MODAL,
			deliverables: projectDeliverables,
			projectId: phase?.projectId,
			baselineItems,
			budgetType,
		});
	};

	const contextMenuOptions: ContextMenuOption[] = editMode
		? [
				{
					name: formatMessage({id: 'deliverable.change_deliverable'}),
					onClick: showChangeDeliverableModal,
				},
				...(deliverable
					? [
							{
								name: formatMessage({id: 'deliverable.remove_deliverable_from_phase'}),
								onClick: showDeleteDeliverableModal,
							},
					  ]
					: []),
		  ]
		: [
				{
					name: formatMessage({id: 'deliverables.manage.modal.manage_deliverables'}),
					onClick: showManageDeliverablesModal,
					disabled: baselineLocked,
				},
		  ];

	return (
		<NestedCard colorTheme="medium" dataCy={'deliverable-nested-card'}>
			<NestedCard.Header
				{...(deliverable
					? {
							mainIcon: 'deliverable',
							sectionName: formatMessage({id: 'common.deliverable'}),
					  }
					: {sectionName: formatMessage({id: 'deliverable.no_deliverable'})})}
				name={deliverable?.name || ''}
				showContextMenu={true}
				contextMenuOptions={contextMenuOptions}
			></NestedCard.Header>
			{!!baselineItems?.length && (
				<NestedCard.SummaryContent>
					<FinancialsSummary currency={currency} phaseBaselineTotals={phaseDeliverableBaselineTotals} />
				</NestedCard.SummaryContent>
			)}
			<NestedCard.CollapsibleContentWrapper>
				<NestedCard.Content>
					<BaselineEstimateTables
						editMode={editMode}
						company={company}
						currency={currency}
						setProjectBaselineItems={setProjectBaselineItems}
						projectBudget={projectBudget}
						isFixedPrice={isFixedPrice}
						groupedProjectTotals={groupedProjectTotals}
						phase={phase}
						phaseDeliverable={phaseDeliverable}
						baselineItems={baselineItems || []}
						eyeOptionMap={eyeOptionMap}
						useDeliverables={true}
						disabledRoleIds={disabledRoleIds}
					/>
				</NestedCard.Content>
			</NestedCard.CollapsibleContentWrapper>
		</NestedCard>
	);
};
